import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Trophy, CheckCircle, XCircle, Lightbulb, Loader2, Repeat, 
  Star, Zap, Crown, Sparkles, TrendingUp, Award, ArrowLeft
} from 'lucide-react';
import { useContext } from 'react';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExerciseResultsDuolingo = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { setUser } = useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
    } catch (error) {
      toast.error('Çıkış yapılırken hata oluştu');
    }
  };
  
  useEffect(() => {
    loadResults();
  }, [sessionId]);
  
  const loadResults = async () => {
    try {
      const response = await axios.get(`${API}/exercises/history`, {
        withCredentials: true
      });
      const foundSession = response.data.find(s => s.id === sessionId);
      if (foundSession) {
        setSession(foundSession);
        if (foundSession.score >= 80) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }
      } else {
        toast.error('Sonuçlar bulunamadı');
        navigate('/exercises');
      }
    } catch (error) {
      toast.error('Sonuçlar yüklenemedi');
      navigate('/exercises');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-xl font-bold text-gray-700">Sonuçlar hesaplanıyor...</p>
        </div>
      </div>
    );
  }
  
  if (!session || !session.answers) return null;
  
  const correctCount = session.answers.filter(a => a.is_correct).length;
  const totalExercises = session.answers.length;
  const score = session.score || 0;
  
  const getScoreEmoji = () => {
    if (score >= 90) return '🏆';
    if (score >= 80) return '🎉';
    if (score >= 70) return '😊';
    if (score >= 60) return '👍';
    return '💪';
  };
  
  const getScoreMessage = () => {
    if (score >= 90) return 'Muhteşem! Mükemmel performans!';
    if (score >= 80) return 'Harika! Çok iyi gidiyorsun!';
    if (score >= 70) return 'Güzel! İyi bir sonuç!';
    if (score >= 60) return 'Fena değil! Devam et!';
    return 'Biraz daha pratik yapmalısın!';
  };
  
  const xpEarned = Math.floor(score / 10) * 10; // 10 Mil per 10%
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8 relative overflow-hidden">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-fall"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-20px`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            >
              {['🎉', '⭐', '🏆', '✨', '🎊'][Math.floor(Math.random() * 5)]}
            </div>
          ))}
        </div>
      )}
      
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Top Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-gray-300 hover:bg-gray-50 font-semibold px-6 py-3 rounded-xl w-full sm:w-auto"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Geri
          </Button>
          <Button
            onClick={() => navigate('/exercises')}
            size="lg"
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold px-6 py-3 rounded-xl shadow-lg w-full sm:w-auto"
          >
            <Trophy className="w-5 h-5 mr-2" />
            Diğer Egzersizler 🎯
          </Button>
        </div>
        
        {/* Main Score Card */}
        <Card className="border-4 border-white bg-white shadow-2xl mb-8">
          <CardContent className="p-8 text-center">
            {/* Trophy Icon */}
            <div className="relative inline-block mb-6">
              <div className={`w-32 h-32 rounded-full flex items-center justify-center shadow-2xl ${
                score >= 80 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 animate-bounce' :
                score >= 60 ? 'bg-gradient-to-br from-blue-400 to-blue-500' :
                'bg-gradient-to-br from-gray-400 to-gray-500'
              }`}>
                {score >= 80 ? (
                  <Crown className="w-20 h-20 text-white" />
                ) : score >= 60 ? (
                  <Trophy className="w-20 h-20 text-white" />
                ) : (
                  <TrendingUp className="w-20 h-20 text-white" />
                )}
              </div>
              {score >= 80 && (
                <div className="absolute -top-2 -right-2 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center border-4 border-white animate-pulse">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
              )}
            </div>
            
            {/* Score */}
            <p className="text-7xl font-black mb-4">
              <span className={`${
                score >= 80 ? 'text-green-600' :
                score >= 60 ? 'text-blue-600' :
                'text-orange-600'
              }`}>{score.toFixed(0)}%</span>
            </p>
            
            {/* Message */}
            <p className="text-3xl font-black text-gray-900 mb-6">
              {getScoreEmoji()} {getScoreMessage()}
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-6">
              <div className="p-4 bg-green-100 border-2 border-green-300 rounded-2xl">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-3xl font-black text-green-600">{correctCount}</p>
                <p className="text-sm font-bold text-gray-700">Doğru</p>
              </div>
              <div className="p-4 bg-red-100 border-2 border-red-300 rounded-2xl">
                <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="text-3xl font-black text-red-600">{totalExercises - correctCount}</p>
                <p className="text-sm font-bold text-gray-700">Yanlış</p>
              </div>
              <div className="p-4 bg-purple-100 border-2 border-purple-300 rounded-2xl">
                <Zap className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-3xl font-black text-purple-600">+{xpEarned}</p>
                <p className="text-sm font-bold text-gray-700"> Mil</p>
              </div>
            </div>
            
            {/* Level Badge */}
            <div className="inline-block px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shadow-lg">
              <span className="text-white font-black text-lg">{session.level} Seviyesi</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Detailed Answers */}
        <Card className="border-4 border-white bg-white shadow-2xl mb-8">
          <CardContent className="p-6">
            <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Detaylı İnceleme
            </h3>
            
            <div className="space-y-4">
              {session.answers.map((answer, idx) => (
                <Card
                  key={idx}
                  className={`border-2 ${
                    answer.is_correct
                      ? 'border-green-300 bg-green-50'
                      : 'border-red-300 bg-red-50'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-lg">#{idx + 1}</span>
                        {answer.is_correct ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-600" />
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-900 font-semibold mb-3">{answer.question}</p>
                    
                    {!answer.is_correct && (
                      <div className="grid md:grid-cols-2 gap-3 mb-3">
                        <div className="p-3 bg-red-100 border-2 border-red-300 rounded-xl">
                          <p className="text-xs font-bold text-gray-600 mb-1">Senin cevabın:</p>
                          <p className="text-sm font-semibold text-red-700">
                            {session.exercises[idx]?.options[answer.user_answer]}
                          </p>
                        </div>
                        <div className="p-3 bg-green-100 border-2 border-green-300 rounded-xl">
                          <p className="text-xs font-bold text-gray-600 mb-1">Doğru cevap:</p>
                          <p className="text-sm font-semibold text-green-700">
                            {session.exercises[idx]?.options[answer.correct_answer]}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {answer.is_correct && (
                      <div className="p-3 bg-green-100 border-2 border-green-300 rounded-xl mb-3">
                        <p className="text-xs font-bold text-gray-600 mb-1">✅ Doğru cevap:</p>
                        <p className="text-sm font-semibold text-green-700">
                          {session.exercises[idx]?.options[answer.correct_answer]}
                        </p>
                      </div>
                    )}
                    
                    {/* Explanation */}
                    {(answer.explanation_tr || answer.explanation_en) && (
                      <div className="p-3 bg-blue-50 border-2 border-blue-200 rounded-xl">
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            {answer.explanation_tr && (
                              <p className="text-sm text-gray-800 mb-2">
                                <span className="font-bold">🇹🇷</span> {answer.explanation_tr}
                              </p>
                            )}
                            {answer.explanation_en && (
                              <p className="text-sm text-gray-700">
                                <span className="font-bold">🇬🇧</span> {answer.explanation_en}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button
            onClick={() => navigate(`/exercises/${session.category}/${session.level}/${session.topic_or_theme}`)}
            size="lg"
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-black text-xl px-8 py-6 rounded-2xl shadow-2xl"
          >
            <Repeat className="w-6 h-6 mr-2" />
            Tekrar Yap 🔄
          </Button>
        </div>
        
        {/* Motivational Message */}
        {score >= 80 && (
          <div className="mt-8 text-center">
            <div className="inline-block bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400 p-6 rounded-3xl shadow-2xl">
              <p className="text-2xl font-black text-white mb-2">🎉 Süpersin! 🎉</p>
              <p className="text-white">Böyle devam et, şampiyonsun!</p>
            </div>
          </div>
        )}
        
        {score < 60 && (
          <div className="mt-8 text-center">
            <div className="inline-block bg-gradient-to-r from-blue-400 to-blue-500 p-6 rounded-3xl shadow-2xl">
              <p className="text-2xl font-black text-white mb-2">💪 Devam Et!</p>
              <p className="text-white">Pratik yapmaya devam et, başarırsın!</p>
            </div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-fall {
          animation: fall linear forwards;
        }
      `}</style>
    </div>
  );
};

export default ExerciseResultsDuolingo;
